import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Card, Btn, Modal, Field, Input, Select, Badge, EmptyState } from "../components/kr";
import { DatePicker } from "../components/DatePicker";
import { MoneyInput } from "../components/MoneyInput";
import { useConfirm } from "../components/ConfirmDialog";
import { useAuth } from "../lib/auth";
import { useMultiSelect } from "../lib/multiSelect";
import MultiSelectBar from "../components/MultiSelectBar";
import { toast } from "sonner";
import { Plus, Trash, Check, X, ClockCounterClockwise } from "@phosphor-icons/react";
import { Pagination, usePagination } from "../components/Pagination";

const CATS = ["equipment", "packaging", "consumables", "utilities", "maintenance", "other"];
const CAT_TONE = { equipment: "orange", packaging: "yellow", consumables: "zinc", utilities: "green", maintenance: "red", other: "zinc" };
const STATUS_TONE = { pending: "yellow", approved: "green", rejected: "red" };

export default function MiscPurchases() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const ms = useMultiSelect();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [rejecting, setRejecting] = useState(null);
  const [filter, setFilter] = useState("all"); // all | pending | approved | rejected
  const { confirm } = useConfirm();

  const load = async () => setItems((await api.get("/misc-purchases")).data);
  useEffect(() => { load(); }, []);

  const del = async (i) => {
    const isMine = i.created_by === user?.email;
    const canDelete = isAdmin || (isMine && i.status === "pending");
    if (!canDelete) {
      toast.error("You can only cancel your own pending requests");
      return;
    }
    const ok = await confirm({
      title: isAdmin ? "Delete expense entry?" : "Cancel your request?",
      message: `${i.vendor} · ₹${i.amount.toLocaleString("en-IN")}${i.status === "approved" ? "\n\nThe linked accounting entry will also be removed." : ""}`,
      tone: isAdmin ? "danger" : "warning",
      confirmLabel: isAdmin ? "Delete" : "Cancel Request",
    });
    if (!ok) return;
    await api.delete(`/misc-purchases/${i.id}`);
    load();
  };

  const approve = async (i) => {
    const ok = await confirm({
      title: "Approve expense?",
      message: `${i.vendor} · ₹${i.amount.toLocaleString("en-IN")} · ${i.category}\n\nWill create an accounting entry.`,
      tone: "warning",
      confirmLabel: "Approve",
    });
    if (!ok) return;
    try {
      await api.post(`/misc-purchases/${i.id}/approve`, { reason: "" });
      toast.success("Expense approved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const filtered = filter === "all" ? items : items.filter(i => (i.status || "approved") === filter);
  const pending = items.filter(i => i.status === "pending").length;
  const total = items.reduce((s, i) => s + (i.status !== "rejected" ? (i.amount || 0) : 0), 0);

  const { page, setPage, pageSize, setPageSize } = usePagination(20);
  useEffect(() => { setPage(1); }, [filter, setPage]);
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="p-6 lg:p-10">
      <PageHeader
        label="OPERATIONAL SPEND"
        title={isAdmin ? "Expenses & Requests" : "My Expense Requests"}
        subtitle={
          isAdmin
            ? `${pending} pending · Total non-rejected: ₹${total.toLocaleString("en-IN")}`
            : "Submit a purchase you made on the company's behalf, or a company purchase that needs admin approval."
        }
        action={<Btn onClick={() => setOpen(true)} data-testid="add-misc"><Plus size={14} /> {isAdmin ? "New Purchase" : "New Request"}</Btn>}
      />

      <div className="mb-4 flex gap-2 flex-wrap">
        {["all", "pending", "approved", "rejected"].map(k => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`px-3 py-1 text-[11px] font-bold uppercase tracking-widest border ${filter === k ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-300 hover:border-orange-500"}`}
            data-testid={`misc-filter-${k}`}
          >
            {k} <span className="opacity-60 font-mono">({k === "all" ? items.length : items.filter(i => (i.status || "approved") === k).length})</span>
          </button>
        ))}
      </div>

      <Card className="!p-0">
        {filtered.length === 0 ? (
          <div className="p-6"><EmptyState title="No entries in this bucket" /></div>
        ) : (
          <table className="kr-table">
            <thead>
              <tr>
                {isAdmin && (
                  <th className="w-8">
                    <input
                      type="checkbox"
                      checked={ms.selected.length > 0 && ms.selected.length === filtered.length}
                      onChange={() => ms.toggleAll(filtered.map(x => x.id))}
                      className="accent-orange-600"
                      data-testid="misc-check-all"
                    />
                  </th>
                )}
                <th>Date</th><th>Vendor</th><th>Category</th><th>Amount</th>
                <th>Payment</th><th>Status</th><th>Requester</th><th>Notes</th><th></th>
              </tr>
            </thead>
            <tbody>
              {paged.map(i => {
                const status = i.status || "approved";
                return (
                  <tr key={i.id} data-testid={`misc-row-${i.id}`}>
                    {isAdmin && (
                      <td>
                        <input
                          type="checkbox"
                          checked={ms.isSelected(i.id)}
                          onChange={() => ms.toggle(i.id)}
                          className="accent-orange-600"
                          data-testid={`misc-check-${i.id}`}
                        />
                      </td>
                    )}
                    <td className="font-mono text-xs">{i.date}</td>
                    <td className="font-bold">
                      {i.vendor}
                      {i.mode === "personal" && (
                        <div className="text-[10px] text-orange-700 font-bold uppercase tracking-widest">Personal (reimburse)</div>
                      )}
                    </td>
                    <td><Badge tone={CAT_TONE[i.category]}>{i.category}</Badge></td>
                    <td className="font-mono font-bold">₹{i.amount.toLocaleString("en-IN")}</td>
                    <td><Badge tone={i.already_paid ? "green" : "yellow"}>{i.already_paid ? "paid" : "pending"}</Badge></td>
                    <td>
                      <Badge tone={STATUS_TONE[status] || "zinc"}>{status}</Badge>
                      {status === "rejected" && i.reject_reason && (
                        <div className="text-[10px] text-red-700 mt-1 max-w-[200px]" title={i.reject_reason}>
                          {i.reject_reason.length > 40 ? i.reject_reason.slice(0, 40) + "…" : i.reject_reason}
                        </div>
                      )}
                    </td>
                    <td className="text-xs font-mono text-zinc-500">{i.created_by_name || i.created_by || "—"}</td>
                    <td className="text-xs text-zinc-600 max-w-xs">{i.notes}</td>
                    <td>
                      <div className="flex items-center gap-2 justify-end">
                        {isAdmin && status === "pending" && (
                          <>
                            <button onClick={() => approve(i)} className="text-green-600 hover:text-green-800" title="Approve">
                              <Check size={16} weight="bold" />
                            </button>
                            <button onClick={() => setRejecting(i)} className="text-red-600 hover:text-red-800" title="Reject">
                              <X size={16} weight="bold" />
                            </button>
                          </>
                        )}
                        {status === "pending" && !isAdmin && (
                          <span title="Awaiting approval" className="text-yellow-600">
                            <ClockCounterClockwise size={16} weight="bold" />
                          </span>
                        )}
                        <button onClick={() => del(i)} className="text-red-600 hover:text-red-800" title="Delete/Cancel">
                          <Trash size={14} weight="bold" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <Pagination
          page={page}
          pageSize={pageSize}
          total={filtered.length}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          testId="misc-pager"
        />
      </Card>

      {open && <MiscModal user={user} isAdmin={isAdmin} onClose={() => setOpen(false)} onAdded={() => { setOpen(false); load(); }} />}
      {rejecting && (
        <RejectModal
          row={rejecting}
          onClose={() => setRejecting(null)}
          onDone={() => { setRejecting(null); load(); }}
        />
      )}
      <MultiSelectBar
        count={ms.selected.length}
        ids={ms.selected}
        resource="misc-purchases"
        label="expenses"
        onCleared={() => { ms.clear(); load(); }}
      />
    </div>
  );
}

function MiscModal({ user, isAdmin, onClose, onAdded }) {
  const [f, setF] = useState({
    vendor: "", category: "other", amount: "",
    date: new Date().toISOString().slice(0, 10), notes: "",
    already_paid: !isAdmin,   // employees usually already paid out of pocket
    mode: isAdmin ? "company" : "personal",
  });
  const upd = (k, v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/misc-purchases", { ...f, amount: parseFloat(f.amount) });
      toast.success(isAdmin ? "Added — also posted to accounting" : "Request sent — awaiting admin approval");
      onAdded();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Modal open onClose={onClose} title={isAdmin ? "New Purchase" : "New Expense Request"}>
      <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="misc-form">
        <Field label="Vendor / Store Name" full><Input value={f.vendor} onChange={e => upd("vendor", e.target.value)} required data-testid="misc-vendor" /></Field>
        <Field label="Category">
          <Select value={f.category} onChange={e => upd("category", e.target.value)} data-testid="misc-category">
            {CATS.map(c => <option key={c}>{c}</option>)}
          </Select>
        </Field>
        <Field label="Amount"><MoneyInput value={f.amount} onChange={e => upd("amount", e.target.value)} min={0} required placeholder="₹0.00" className="w-full px-3 py-2 border border-zinc-300 text-sm font-mono" data-testid="misc-amount" /></Field>
        <Field label="Date"><DatePicker value={f.date} onChange={e => upd("date", e.target.value)} required /></Field>
        <Field label="Whose money?">
          <Select value={f.mode} onChange={e => upd("mode", e.target.value)} data-testid="misc-mode">
            <option value="company">Company money</option>
            <option value="personal">My personal money (needs reimbursement)</option>
          </Select>
        </Field>
        <Field label="Payment status">
          <Select value={f.already_paid ? "yes" : "no"} onChange={e => upd("already_paid", e.target.value === "yes")}>
            <option value="no">Not yet paid (creates payable)</option>
            <option value="yes">Already paid (creates cleared entry)</option>
          </Select>
        </Field>
        <Field label="Notes / description" full>
          <textarea
            rows={3}
            value={f.notes}
            onChange={e => upd("notes", e.target.value)}
            className="w-full border border-zinc-300 px-3 py-2 focus:outline-none focus:border-orange-500 resize-none text-sm"
            placeholder="What was this for? Why is it needed?"
          />
        </Field>
        {!isAdmin && (
          <div className="col-span-2 bg-yellow-50 border-l-4 border-yellow-500 p-3 text-xs">
            <b>Approval required:</b> An admin will review your request. You&apos;ll get a notification on approval or rejection.
          </div>
        )}
        <div className="col-span-2 flex justify-end gap-2">
          <Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" data-testid="misc-submit">{isAdmin ? "Add" : "Send Request"}</Btn>
        </div>
      </form>
    </Modal>
  );
}

function RejectModal({ row, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/misc-purchases/${row.id}/reject`, { reason });
      toast.success("Rejected — requester notified");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Reject expense request">
      <form onSubmit={submit} className="space-y-4" data-testid="misc-reject-form">
        <div className="bg-zinc-50 border border-zinc-200 p-3 text-xs font-mono">
          {row.vendor} · ₹{row.amount.toLocaleString("en-IN")} · {row.category}
          <div className="text-zinc-500 mt-1">by {row.created_by_name || row.created_by}</div>
        </div>
        <Field label="Reason (visible to requester)">
          <textarea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            className="w-full border border-zinc-300 px-3 py-2 focus:outline-none focus:border-orange-500 resize-none text-sm"
            placeholder="Explain why this is being rejected."
          />
        </Field>
        <div className="flex justify-end gap-2">
          <Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={busy}>{busy ? "Rejecting…" : "Reject"}</Btn>
        </div>
      </form>
    </Modal>
  );
}
